import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { clientsApi, Client, CreateClientData, UpdateClientData } from '../../api/clientsApi';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import { TableFilters, ColumnConfig, FilterConfig } from '../../components/common/TableFilters';
import { PhoneInput } from '../../components/common/PhoneInput';
import { downloadBlob } from '../../utils/helpers';
import './Clients.css';

const PER_PAGE = 50;

// Column configuration
const ALL_COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'code', label: 'Code', defaultVisible: true },
  { key: 'contact_name', label: 'Contact', defaultVisible: true },
  { key: 'contact_email', label: 'Email', defaultVisible: true },
  { key: 'contact_phone', label: 'Phone', defaultVisible: true },
  { key: 'description', label: 'Description', defaultVisible: false },
  { key: 'status', label: 'Status', defaultVisible: true },
];

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);

export function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filter state - default to showing active clients only
  const [filterValues, setFilterValues] = useState<Record<string, string | number | undefined>>({ is_active: 'true' });
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);
  
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<CreateClientData & { is_active?: boolean }>({
    name: '',
    code: '',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    is_active: true,
  });

  // Build filter configs
  const filterConfigs: FilterConfig[] = [
    {
      key: 'is_active',
      label: 'Status',
      options: [
        { value: 'true', label: 'Active Only' },
        { value: 'false', label: 'Inactive Only' },
      ],
    },
  ];

  const fetchClients = useCallback(async (filters: Record<string, string | number | undefined> = {}) => {
    try {
      setLoading(true);
      setError(null);
      const res = await clientsApi.getClients({
        is_active: filters.is_active as string | undefined,
        // When no specific status filter is set (All), tell the backend to include inactive clients
        include_inactive: !filters.is_active,
        search: searchTerm || undefined,
      });
      setClients(res.clients);
      setCurrentPage(1); // Reset to page 1
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(clients.length / PER_PAGE);
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * PER_PAGE;
    return clients.slice(startIndex, startIndex + PER_PAGE);
  }, [clients, currentPage]);
  const startItem = clients.length > 0 ? (currentPage - 1) * PER_PAGE + 1 : 0;
  const endItem = Math.min(currentPage * PER_PAGE, clients.length);

  useEffect(() => {
    fetchClients({ is_active: 'true' });
  }, []);

  const handleSearch = () => {
    fetchClients(filterValues);
  };

  const handleFilterChange = (key: string, value: string | number | undefined) => {
    const newFilters = { ...filterValues, [key]: value };
    setFilterValues(newFilters);
    fetchClients(newFilters);
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      setExportLoading(true);
      const blob = await clientsApi.exportClients({
        format,
        search: searchTerm || undefined,
        is_active: filterValues.is_active as string | undefined,
        columns: visibleColumns.join(','),
      });
      downloadBlob(blob, `clients.${format}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export');
    } finally {
      setExportLoading(false);
    }
  };

  const validateClientForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Client name is required.';
    else if (formData.name.length > 120) errors.name = 'Client name must be 120 characters or fewer.';
    if (formData.code && formData.code.length > 20) errors.code = 'Code must be 20 characters or fewer.';
    if (formData.contact_name && formData.contact_name.length > 60) errors.contact_name = 'Contact name must be 60 characters or fewer.';
    if (formData.contact_phone) {
      // extract the digits after the dial code (everything that is not the leading +XX part)
      const digitsOnly = formData.contact_phone.replace(/^\+\d+/, '');
      if (!/^\d*$/.test(digitsOnly)) errors.contact_phone = 'Phone number must contain digits only.';
      else if (digitsOnly.length > 10) errors.contact_phone = 'Phone number must be 10 digits or fewer.';
    }
    if (formData.description && formData.description.length > 120) errors.description = 'Description must be 120 characters or fewer.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateClientForm()) return;
    try {
      setLoading(true);
      const res = await clientsApi.createClient(formData);
      setClients([...clients, res.client]);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClient) return;
    if (!validateClientForm()) return;
    try {
      setLoading(true);
      const updateData: UpdateClientData = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        is_active: formData.is_active,
      };
      const res = await clientsApi.updateClient(editingClient.id, updateData);
      setClients(clients.map(c => c.id === editingClient.id ? res.client : c));
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update client');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this client?')) return;
    try {
      setLoading(true);
      await clientsApi.deleteClient(clientId);
      await fetchClients(filterValues);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate client');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      code: client.code || '',
      description: client.description || '',
      contact_name: client.contact_name || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      is_active: client.is_active,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingClient(null);
    setFormErrors({});
    setFormData({
      name: '',
      code: '',
      description: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      is_active: true,
    });
  };

  return (
    <div className="clients-page">
      <div className="clients-header">
        <div className="clients-title">
          <h1>Client Management</h1>
          <p>Manage all clients and their information</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Icons.Plus /> Add Client
        </Button>
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <Card>
        <TableFilters
          searchPlaceholder="Search clients..."
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          onSearch={handleSearch}
          filters={filterConfigs}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          columns={ALL_COLUMNS}
          visibleColumns={visibleColumns}
          onColumnToggle={setVisibleColumns}
          onExport={handleExport}
          exportLoading={exportLoading}
          onRefresh={() => fetchClients(filterValues)}
          refreshLoading={loading}
          totalCount={clients.length}
          shownCount={paginatedClients.length}
          countLabel="Clients"
        />

        <div className="clients-table-wrapper">
          <table className="clients-table">
            <thead>
              <tr>
                {visibleColumns.includes('name') && <th>Name</th>}
                {visibleColumns.includes('code') && <th>Code</th>}
                {visibleColumns.includes('contact_name') && <th>Contact</th>}
                {visibleColumns.includes('contact_email') && <th>Email</th>}
                {visibleColumns.includes('contact_phone') && <th>Phone</th>}
                {visibleColumns.includes('description') && <th>Description</th>}
                {visibleColumns.includes('status') && <th>Status</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedClients.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="empty-state">
                    <Icons.Building />
                    <p>No clients found</p>
                  </td>
                </tr>
              ) : (
                paginatedClients.map((client) => (
                  <tr key={client.id} className={!client.is_active ? 'inactive' : ''}>
                    {visibleColumns.includes('name') && (
                      <td>
                        <div className="client-name-cell">
                          <div className="client-name-text">{client.name}</div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.includes('code') && <td>{client.code || '-'}</td>}
                    {visibleColumns.includes('contact_name') && <td>{client.contact_name || '-'}</td>}
                    {visibleColumns.includes('contact_email') && <td>{client.contact_email || '-'}</td>}
                    {visibleColumns.includes('contact_phone') && <td>{client.contact_phone || '-'}</td>}
                    {visibleColumns.includes('description') && <td>{client.description || '-'}</td>}
                    {visibleColumns.includes('status') && (
                      <td>
                        <span className={`status-badge ${client.is_active ? 'active' : 'inactive'}`}>
                          {client.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    )}
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn edit"
                          onClick={() => openEditModal(client)}
                          title="Edit"
                        >
                          <Icons.Edit />
                        </button>
                        {client.is_active && (
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteClient(client.id)}
                            title="Deactivate"
                          >
                            <Icons.Trash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {clients.length > 0 ? startItem : 0} to {endItem} of {clients.length} clients
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              <Icons.ChevronsLeft />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <Icons.ChevronLeft />
            </button>

            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn page-num ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <Icons.ChevronRight />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              <Icons.ChevronsRight />
            </button>
          </div>
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
              <button className="modal-close" onClick={resetForm}>
                <Icons.X />
              </button>
            </div>
            <form onSubmit={editingClient ? handleUpdateClient : handleCreateClient}>
              <div className="form-group">
                <label>Client Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter client name"
                  maxLength={120}
                />
                {formErrors.name && <span className="field-error">{formErrors.name}</span>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Code</label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g., ACME"
                    maxLength={20}
                  />
                  {formErrors.code && <span className="field-error">{formErrors.code}</span>}
                </div>
                <div className="form-group">
                  <label>Contact Name</label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Contact person"
                    maxLength={60}
                  />
                  {formErrors.contact_name && <span className="field-error">{formErrors.contact_name}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email</label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <PhoneInput
                    value={formData.contact_phone || ''}
                    onChange={(val) => setFormData({ ...formData, contact_phone: val })}
                  />
                  {formErrors.contact_phone && <span className="field-error">{formErrors.contact_phone}</span>}
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the client"
                  rows={3}
                  maxLength={120}
                />
                <div className="char-count">
                  {(formData.description || '').length}/120
                </div>
                {formErrors.description && <span className="field-error">{formErrors.description}</span>}
              </div>
              {editingClient && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              )}
              <div className="modal-actions">
                <Button type="button" variant="secondary" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : (editingClient ? 'Update Client' : 'Create Client')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
